import React, { useState } from "react";
import { useDispatch } from "react-redux";
import DayJS from "react-dayjs";
import {
    Alert,
    Box,
    Button,
    CardContent,
    Grid,
    Typography,
} from "@mui/material";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import WarningAmberIcon from "@mui/icons-material/WarningAmber";
import RadioButtonCheckedIcon from "@mui/icons-material/RadioButtonChecked";
import RadioButtonUncheckedIcon from "@mui/icons-material/RadioButtonUnchecked";
import {
    addEnrolment,
    updateEnrolment,
    deleteEnrolment,
} from "../../../../actions/tmsActions";
import ProgressBar from "../../../Common/ProgressBar/ProgressBar";
import ConfirmDialog from "../../../Common/Dialog/ConfirmDialog";

const DateText = ({ date }) =>
    !!date ? (
        <DayJS format="DD MMM YYYY">{date}</DayJS>
    ) : (
        <span className="sr-only">No data available</span>
    );

function CourseEnrolmentDetails({ course }) {
    const dispatch = useDispatch();
    const [dialogOpen, setDialogOpen] = useState(null);

    if (!course) {
        return null;
    }

    const enrolment = course?.enrolment ?? {};
    if (!enrolment) {
        return null;
    }

    const enrolmentMethod =
        enrolment.method === "assigned" ? "Assigned" : "Enrolled";

    let moduleCount = 0;
    let moduleCompleted = 0;
    // let isCancelled = false;
    // let isCompleted = false;
    // let isWaiting = false;
    let showProgress = false;
    let showDetails = false;
    let button = null;
    let buttonLabel = null;
    let dialogOptions = null;

    if (course.modules) {
        course.modules.forEach((module) => {
            if (module?.enrolment?.status === "completed") {
                moduleCompleted++;
            }
            moduleCount++;
        });
    }

    const progressData = {
        title: "Progress",
        label: "/ " + moduleCount,
        total: moduleCompleted,
        percentage:
            moduleCompleted > 0 ? (moduleCompleted / moduleCount) * 100 : 0,
    };

    const appCode = "C-TMS";
    const enrolOptions = {
        title: "Enrol on course",
        message: `This will enrol you onto the following course: ${
            (course && course.name) || `Enrolment ${enrolment.id}`
        }`,
        label: "Enrol",
        action: () => dispatch(addEnrolment(appCode, course.id)),
    };
    const cancelOptions = {
        title: "Unenroll from course",
        message: `The following enrolment will be cancelled: ${course.name}`,
        label: "Unenrol",
        action: () => dispatch(deleteEnrolment(appCode, enrolment.id)),
    };
    const retakeOptions = {
        title: "Retake course",
        message: `Are you sure you wish to retake ${course.name}?`,
        label: "Retake",
        action: () => {
            const params = {
                status: "enrolled",
                retakeModules:
                    !!course.modules && course.modules.map(({ id }) => id),
            };
            dispatch(updateEnrolment(appCode, enrolment.id, params));
        },
    };

    const mandatoryIcon = enrolment?.isMandatory ? (
        <RadioButtonCheckedIcon fontSize="medium" />
    ) : (
        <RadioButtonUncheckedIcon fontSize="medium" />
    );

    switch (enrolment.status) {
        case "enrolled":
            enrolment.message = (
                <Alert severity="info" icon={mandatoryIcon}>
                    <strong>{enrolmentMethod}</strong> on{" "}
                    <DayJS format="DD MMM YYYY">{enrolment.date}</DayJS>
                </Alert>
            );
            showProgress = true;
            if (!!enrolment.canCancel) {
                button = "Unenrol";
                buttonLabel = `Unenrol from ${course.name}`;
                dialogOptions = cancelOptions;
            }
            break;

        case "waiting":
            enrolment.message = (
                <Alert severity="info" icon={mandatoryIcon}>
                    Added to <strong>Waiting list</strong>
                </Alert>
            );
            // isWaiting = true;
            if (!!enrolment.canCancel) {
                button = "Unenrol";
                buttonLabel = `Unenrol from ${course.name}`;
                dialogOptions = cancelOptions;
            }
            break;

        case "cancelled":
            enrolment.message = (
                <Alert
                    severity="warning"
                    icon={<WarningAmberIcon fontSize="medium" />}
                >
                    Enrolment has been <strong>Cancelled</strong>
                </Alert>
            );
            // isCancelled = true;
            if (!!course.canEnrol) {
                button = "Enrol";
                buttonLabel = `Enrol into ${course.name}`;
                dialogOptions = enrolOptions;
            }
            break;

        case "completed":
            enrolment.message = (
                <Alert
                    severity="success"
                    icon={<CheckCircleIcon fontSize="medium" />}
                >
                    <strong>Completed</strong>
                </Alert>
            );
            //isCompleted = true;
            showDetails = true;
            if (!!enrolment.canRetake) {
                button = "Retake";
                buttonLabel = `Retake ${course.name}`;
                dialogOptions = retakeOptions;
            }
            break;

        default:
            if (!!course.canEnrol) {
                button = "Enrol";
                buttonLabel = `Enrol into ${course.name}`;
                dialogOptions = enrolOptions;
            }
            break;
    }

    return (
        <CardContent>
            <Box className="card-content">
                <Grid container spacing={4}>
                    {enrolment.message && (
                        <Grid item xs={12} sm={12} md={12}>
                            {enrolment.message}
                        </Grid>
                    )}

                    {showProgress && (
                        <Grid item xs={12} sm={12} md={12}>
                            <ProgressBar data={progressData} />
                        </Grid>
                    )}

                    {showDetails && (
                        <Grid item xs={12} sm={12} md={6}>
                            <Grid container spacing={4}>
                                <Grid item xs={12}>
                                    <Typography variant="body2">
                                        <strong>{enrolmentMethod}</strong>&nbsp;
                                        <DateText date={enrolment.date} />
                                    </Typography>
                                    <Typography variant="body2">
                                        <strong>Started</strong>&nbsp;
                                        <DateText
                                            date={enrolment.dateCommenced}
                                        />
                                    </Typography>
                                    <Typography variant="body2">
                                        <strong>Completed</strong>&nbsp;
                                        <DateText
                                            date={enrolment.dateCompleted}
                                        />
                                    </Typography>
                                </Grid>
                            </Grid>
                        </Grid>
                    )}

                    {showDetails && (
                        <Grid item xs={12} sm={12} md={6}>
                            <Grid container spacing={4}>
                                <Grid item xs={12}>
                                    <Typography variant="body2">
                                        <strong>Mandatory</strong>{" "}
                                        {enrolment?.isMandatory ? "Yes" : "No"}
                                    </Typography>
                                    <Typography variant="body2">
                                        <strong>Can retake</strong>{" "}
                                        {enrolment?.canRetake ? "Yes" : "No"}
                                    </Typography>
                                </Grid>
                            </Grid>
                        </Grid>
                    )}

                    {!!button && (
                        <Grid item xs={12} sm={12} md={12}>
                            <Button
                                aria-label={buttonLabel}
                                variant={
                                    button === "Unenrol"
                                        ? "outlined"
                                        : "contained"
                                }
                                disableElevation={true}
                                onClick={() => setDialogOpen(true)}
                            >
                                {button}
                            </Button>
                        </Grid>
                    )}
                </Grid>
            </Box>
            {!!dialogOpen && !!dialogOptions && (
                <ConfirmDialog
                    open={true}
                    title={dialogOptions.title}
                    content={dialogOptions.message}
                    confirmButtonLabel={dialogOptions.label}
                    onConfirm={dialogOptions.action}
                    onCancel={() => setDialogOpen(false)}
                    onClose={() => setDialogOpen(false)}
                />
            )}
        </CardContent>
    );
}

export default CourseEnrolmentDetails;
